<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-10">
    <div class="max-w-3xl mx-auto space-y-6">
      <div class="text-center">
        <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy" class="w-12 h-12 mx-auto rounded-xl mb-3" />
        <h1 class="text-2xl font-bold text-white">Field Ticket Approval</h1>
        <p class="text-slate-400 text-sm mt-1">Company man sign-off</p>
      </div>

      <p v-if="error" class="text-red-400 text-center">{{ error }}</p>
      <p v-if="message" class="text-emerald-400 text-center">{{ message }}</p>
      <div v-if="loading" class="text-slate-400 text-center py-12">Loading ticket…</div>

      <template v-else-if="ticket">
        <div class="panel">
          <div class="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p class="text-xs text-slate-500 font-mono">{{ ticket.number }}</p>
              <h2 class="text-xl font-bold text-white mt-1">{{ ticket.title }}</h2>
              <p class="text-slate-400 mt-1">{{ ticket.customerName || '—' }} · {{ ticket.siteName || '—' }}</p>
            </div>
            <div class="text-right">
              <p class="text-sm text-slate-400">Total</p>
              <p class="text-2xl font-bold text-emerald-400">${{ Number(ticket.totalAmount || 0).toLocaleString() }}</p>
              <p class="text-xs text-slate-500 mt-1 capitalize">{{ ticket.status }}</p>
            </div>
          </div>

          <table class="w-full text-sm mt-6">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th class="py-2 text-left">Item</th>
                <th class="py-2 text-right">Qty</th>
                <th class="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in ticket.lines || []" :key="line.id" class="border-b border-slate-800 text-slate-200">
                <td class="py-2">
                  <div>{{ line.description }}</div>
                  <div class="text-xs text-slate-500 capitalize">{{ line.lineType }}</div>
                </td>
                <td class="py-2 text-right">{{ line.quantity }} {{ line.unit }}</td>
                <td class="py-2 text-right">${{ Number(line.amount).toLocaleString() }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="canApprove" class="panel space-y-4">
          <div>
            <label class="form-label">Your Name</label>
            <input v-model="name" class="form-input" placeholder="Company man name" />
          </div>
          <div>
            <label class="form-label">Notes (optional)</label>
            <textarea v-model="notes" rows="2" class="form-input"></textarea>
          </div>
          <div class="flex flex-wrap gap-3">
            <button class="btn-primary" :disabled="busy || !name.trim()" @click="decide('approve')">Approve Ticket</button>
            <button class="btn-danger" :disabled="busy" @click="decide('reject')">Reject</button>
          </div>
        </div>

        <div v-else class="panel text-center text-slate-300">
          This ticket is already <span class="capitalize text-white">{{ ticket.status }}</span>.
          <p v-if="ticket.customerSignature" class="text-sm text-slate-500 mt-2">{{ ticket.customerSignature }}</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { buildApiUrl } from '@/config/api'

const route = useRoute()
const loading = ref(true)
const busy = ref(false)
const error = ref('')
const message = ref('')
const ticket = ref<any>(null)
const canApprove = ref(false)
const name = ref('')
const notes = ref('')

const token = () => String(route.params.token || '')

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`ticket-approve.php?token=${encodeURIComponent(token())}`))
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Invalid link')
    ticket.value = data.ticket
    canApprove.value = !!data.canApprove
    name.value = data.ticket?.companyManName || ''
  } catch (e: any) {
    error.value = e.message || 'Failed to load'
  } finally {
    loading.value = false
  }
}

async function decide(decision: 'approve' | 'reject') {
  busy.value = true
  error.value = ''
  message.value = ''
  try {
    const res = await fetch(buildApiUrl('ticket-approve.php'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token(), decision, name: name.value, notes: notes.value }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Action failed')
    ticket.value = data.ticket
    canApprove.value = false
    message.value = decision === 'approve' ? 'Ticket approved. Thank you.' : 'Ticket rejected.'
  } catch (e: any) {
    error.value = e.message || 'Action failed'
  } finally {
    busy.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.panel { @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6; }
.form-label { @apply block text-sm text-slate-400 mb-1; }
.form-input { @apply w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white; }
.btn-primary { @apply inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium disabled:opacity-50; }
.btn-danger { @apply inline-flex items-center px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/40 text-red-300 disabled:opacity-50; }
</style>
